import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/utils";
import { Loader2, Users, Wallet, BookOpen, CalendarCheck, GraduationCap, Bell } from "lucide-react";

export function DashboardPage() {
  const { user } = useAuth();
  const { data: stats, isPending } = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.stats(),
  });

  if (!user) return null;
  const role = user.role;

  // Tile config per role
  const tiles: { label: string; value: string; icon: React.ComponentType<any>; tone: string }[] = [];
  if ((role === "admin" || role === "principal") && stats) {
    tiles.push({ label: "Students", value: String(stats.students ?? "—"), icon: Users, tone: "text-indigo-600" });
    tiles.push({ label: "Staff", value: String(stats.staff ?? "—"), icon: GraduationCap, tone: "text-emerald-600" });
    tiles.push({ label: "Pending fees", value: String(stats.pending_fees ?? "—"), icon: Wallet, tone: "text-amber-600" });
    tiles.push({ label: "Paid fees", value: String(stats.paid_fees ?? "—"), icon: CalendarCheck, tone: "text-sky-600" });
  } else if (role === "teacher" && stats) {
    tiles.push({ label: "My class", value: String(stats.class_size ?? "—"), icon: Users, tone: "text-indigo-600" });
    tiles.push({ label: "Today's classes", value: String(stats.today_classes ?? "—"), icon: CalendarCheck, tone: "text-emerald-600" });
    tiles.push({ label: "Upcoming exams", value: String(stats.upcoming_exams ?? "—"), icon: GraduationCap, tone: "text-amber-600" });
  } else if ((role === "student" || role === "parent") && stats) {
    tiles.push({ label: "Attendance", value: `${stats.attendance_pct ?? "—"}%`, icon: CalendarCheck, tone: "text-emerald-600" });
    tiles.push({ label: "Pending fees", value: String(stats.pending_fees ?? "—"), icon: Wallet, tone: "text-amber-600" });
    tiles.push({ label: "Upcoming exams", value: String(stats.upcoming_exams ?? "—"), icon: GraduationCap, tone: "text-indigo-600" });
    tiles.push({ label: "Unread alerts", value: String(stats.unread_notifications ?? "—"), icon: Bell, tone: "text-sky-600" });
  }

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Welcome back,</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">{user.name.split(" ")[0]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Here&apos;s what&apos;s happening at Little Flowers School today.
          </p>
        </div>
        <Badge variant="outline" className="capitalize">{role}</Badge>
      </div>

      {isPending ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((t) => (
            <Card key={t.label}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{t.label}</CardTitle>
                <t.icon className={`h-5 w-5 ${t.tone}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold tracking-tight">{t.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Quick links</CardTitle>
            <CardDescription>Jump to the most-used parts of the ERP.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            <QuickLink icon={Wallet} label="Fees" desc="Invoices, payments, receipts" to="/fees" />
            <QuickLink icon={GraduationCap} label="Exams" desc="Cycles & report cards" to="/exams" />
            <QuickLink icon={CalendarCheck} label="Attendance" desc="Daily roll-call" to="/attendance" />
            <QuickLink icon={Bell} label="Notifications" desc="Announcements & alerts" to="/notifications" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>System status</CardTitle>
            <CardDescription>All systems operational.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusRow label="Backend API" ok />
            <StatusRow label="Database" ok />
            <StatusRow label="Mobile app sync" ok />
            <StatusRow label="AI assistant" ok />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QuickLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<any>; label: string; desc: string; to: string }) {
  return (
    <a href={to} className="group flex items-center gap-3 rounded-md border bg-card p-3 hover:bg-accent">
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{label}</div>
        <div className="truncate text-xs text-muted-foreground">{desc}</div>
      </div>
    </a>
  );
}

function StatusRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <Badge variant={ok ? "success" : "destructive"}>{ok ? "Healthy" : "Down"}</Badge>
    </div>
  );
}
